import { info, warn } from '@/lib/logger'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PubSubHandler = (data: any) => void
type PubSubLogger = (...args: unknown[]) => void

interface Subscriber {
  event: string
  handler: PubSubHandler
}

const logWarning: PubSubLogger = (...args) => warn('PubSub', ...args)

/** Class responsible for handling event passing between modules in the app */
class PubSub {
  subscribers: Subscriber[]

  /**
   * @constructor
   */
  constructor() {
    this.subscribers = []
  }

  /**
   * Subscribe a provided handler function to an event
   *
   * @param event - Event name to subscribe to
   * @param handler - Function to call when event is fired
   */
  subscribe(event: string, handler: PubSubHandler): void {
    this.subscribers.push({
      event: event,
      handler: handler,
    })
  }

  /**
   * Publish an event and pass its handler some data
   *
   * @param event - Event name to subscribe to
   * @param [data] - Data to be sent to handler function as arguments
   */
  publish(
    event: string,
    data: unknown = {},
    logger: PubSubLogger = logWarning
  ): void {
    info('PubSub', `Published: ${event}`, data)

    let isEventFound = false
    for (const subscriber of this.subscribers) {
      if (subscriber.event === event) {
        isEventFound = true
        try {
          subscriber.handler(data)
        } catch (error) {
          logger(error, {
            tags: {
              module: 'pubsub',
            },
            extra: {
              event,
              data,
            },
          })
        }
      }
    }
    if (!isEventFound) {
      logger(`Pubsub publish no subscribers found "${event}"`, {
        tags: {
          module: 'pubsub',
          event,
        },
      })
    }
  }

  /**
   * Remove a subscriber from an event
   *
   * @param event - Event name to subscribe to
   * @param handler - Handler function that you would like to unsubscribe
   */
  unsubscribe(event: string, handler: PubSubHandler): void {
    this.subscribers = this.subscribers.filter(
      (sub) => !(sub.event === event && sub.handler === handler)
    )
  }
}

// create a singleton of the PubSub class and export that
const pubsub = new PubSub()
export default pubsub
